// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadCustomerPartNumbers from "@steiner/App/Handlers/CustomerPartNumber/LoadCustomerPartNumbers";
import updateSearchField from "@steiner/App/Handlers/CustomerPartNumber/UpdateSearchField";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showTagsOnly = "showTagsOnly",
}

interface ownProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    customerPartNumber: state.xCustomerPartNumbers.customerPartNumber,
    pagination: state.xCustomerPartNumbers.pagination,
});

const mapDispatchToProps = {
    loadCustomerPartNumbers,
    updateSearchField,
};

type Props = ownProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerPartNumbersFiltersStyles {
    container?: GridContainerProps;
    customerPartNumberGridItem?: GridItemProps;
    customerPartNumberTextField?: TextFieldPresentationProps;
    clearButtonGridItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
}

export const filterStyles: CustomerPartNumbersFiltersStyles = {};

const styles = filterStyles;
const CustomerPartNumbersFilters = ({
    fields,
    customerPartNumber,
    pagination,
    loadCustomerPartNumbers,
    updateSearchField,
}: Props) => {
    const [updateTimeoutId, setUpdateTimeoutId] = useState(0);

    useEffect(() => {
        updateParameterAfterTimeout();
    }, [customerPartNumber]);

    const updateParameterAfterTimeout = () => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        setUpdateTimeoutId(
            setTimeout(() => {
                loadCustomerPartNumbers({
                    customerPartNumber,
                    pageSize: pagination?.pageSize || 16,
                    page: 1,
                });
            }, 250) as any,
        );
    };

    const customerPartNumberChangeHandler = (value: string) => {
        updateSearchField({
            customerPartNumber: value,
        });
    };

    const clearButtonHandler = () => {
        updateSearchField({
            customerPartNumber: "",
        });
    };

    if (fields.showTagsOnly) {
        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {customerPartNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            customerPartNumberChangeHandler("");
                        }}
                    >
                        {`${translate("Customer Part #")}: ${customerPartNumber}`}
                    </Tag>
                )}
            </StyledWrapper>
        );
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.customerPartNumberGridItem}>
                <TextField
                    label={translate("Customer Part #")}
                    placeholder={translate("Enter Customer Part #")}
                    value={customerPartNumber}
                    onChange={event => customerPartNumberChangeHandler(event.target.value)}
                    {...styles.customerPartNumberTextField}
                />
            </GridItem>
            <GridItem {...styles.clearButtonGridItem}>
                <Button
                    onClick={clearButtonHandler}
                    {...styles.clearFiltersButton}
                    data-test-selector="customerPartNumber_clearFiltersButton"
                    aria-label="Clear Filters"
                >
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerPartNumbersFilters),
    definition: {
        group: "Customer Part Number" as any,
        displayName: "Customer Part Number Filters",
        allowedContexts: ["CustomerPartNumberPage"],
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
